import { useState, useEffect, useRef } from 'react';
import type { Asset } from '@/types/asset';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Printer, Download } from 'lucide-react';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import { toast } from 'sonner';

interface LabelPrintingModalProps {
  open: boolean;
  onClose: () => void;
  assets: Asset[];
}

interface AveryTemplate {
  id: string;
  name: string;
  description: string;
  labelsPerRow: number;
  labelsPerColumn: number;
  labelWidth: string;
  labelHeight: string;
  pageWidth: string;
  pageHeight: string;
  marginTop: string;
  marginLeft: string;
  gapX: string;
  gapY: string;
}

// Templates ordered by labels per sheet (descending)
const AVERY_TEMPLATES: AveryTemplate[] = [
  {
    id: '5160',
    name: 'Avery 5160',
    description: '1" x 2-5/8" (30 labels per sheet)',
    labelsPerRow: 3,
    labelsPerColumn: 10,
    labelWidth: '2.625in',
    labelHeight: '1in',
    pageWidth: '8.5in',
    pageHeight: '11in',
    marginTop: '0.5in',
    marginLeft: '0.1875in',
    gapX: '0.125in',
    gapY: '0in',
  },
  {
    id: '7610',
    name: 'Avery 7610',
    description: '2.5" x 1.5" (21 labels per sheet)',
    labelsPerRow: 3,
    labelsPerColumn: 7,
    labelWidth: '2.5in',
    labelHeight: '1.5in',
    pageWidth: '8.5in',
    pageHeight: '11in',
    marginTop: '0.5in',
    marginLeft: '0.1875in', // Official Avery spec: 3/16" left margin
    gapX: '0.125in', // Official Avery spec: 1/8" horizontal gap
    gapY: '0in', // Official Avery spec: no vertical gap
  },
  {
    id: '5161',
    name: 'Avery 5161',
    description: '1" x 4" (20 labels per sheet)',
    labelsPerRow: 2,
    labelsPerColumn: 10,
    labelWidth: '4in',
    labelHeight: '1in',
    pageWidth: '8.5in',
    pageHeight: '11in',
    marginTop: '0.5in',
    marginLeft: '0.15625in',
    gapX: '0.1875in',
    gapY: '0in',
  },
  {
    id: '5162',
    name: 'Avery 5162',
    description: '1-1/3" x 4" (14 labels per sheet)',
    labelsPerRow: 2,
    labelsPerColumn: 7,
    labelWidth: '4in',
    labelHeight: '1.33in',
    pageWidth: '8.5in',
    pageHeight: '11in',
    marginTop: '0.83in',
    marginLeft: '0.15625in',
    gapX: '0.1875in',
    gapY: '0in',
  },
  {
    id: '5163',
    name: 'Avery 5163',
    description: '2" x 4" (10 labels per sheet)',
    labelsPerRow: 2,
    labelsPerColumn: 5,
    labelWidth: '4in',
    labelHeight: '2in',
    pageWidth: '8.5in',
    pageHeight: '11in',
    marginTop: '0.5in',
    marginLeft: '0.15625in',
    gapX: '0.1875in',
    gapY: '0in',
  },
  {
    id: '5164',
    name: 'Avery 5164',
    description: '3-1/3" x 4" (6 labels per sheet)',
    labelsPerRow: 2,
    labelsPerColumn: 3,
    labelWidth: '4in',
    labelHeight: '3.33in',
    pageWidth: '8.5in',
    pageHeight: '11in',
    marginTop: '0.5in',
    marginLeft: '0.15625in',
    gapX: '0.1875in',
    gapY: '0in',
  },
].sort((a: any, b: any) => {
  const labelsA = a.labelsPerRow * a.labelsPerColumn;
  const labelsB = b.labelsPerRow * b.labelsPerColumn;
  return labelsB - labelsA; // Sort descending (most labels first)
});

export function LabelPrintingModal({ open, onClose, assets }: LabelPrintingModalProps): React.ReactElement {
  const [selectedTemplate, setSelectedTemplate] = useState<string>(AVERY_TEMPLATES[0].id);
  const [previewReady, setPreviewReady] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  // Validate that all assets have the same code type
  const codeTypes = new Set(assets.map(a => a.codeType).filter(Boolean));
  const hasValidCodeTypes = codeTypes.size === 1 && !codeTypes.has(undefined);
  const codeType = hasValidCodeTypes ? Array.from(codeTypes)[0] : null;

  // Generate preview when modal opens or template changes
  useEffect(() => {
    if (open && hasValidCodeTypes) {
      // Use a small delay to ensure the ref is ready
      const timer = setTimeout(() => {
        if (previewRef.current) {
          generatePreview();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, selectedTemplate, assets, hasValidCodeTypes]);

  const generatePreview = async () => {
    if (!previewRef.current || !codeType) return;

    setPreviewReady(false);
    const template = AVERY_TEMPLATES.find(t => t.id === selectedTemplate);
    if (!template) return;

    // Clear previous preview
    previewRef.current.innerHTML = '';

    // Create preview container
    const previewContainer = document.createElement('div');
    previewContainer.style.cssText = `
      width: ${template.pageWidth};
      height: ${template.pageHeight};
      padding-top: ${template.marginTop};
      padding-left: ${template.marginLeft};
      display: grid;
      grid-template-columns: repeat(${template.labelsPerRow}, ${template.labelWidth});
      grid-template-rows: repeat(${template.labelsPerColumn}, ${template.labelHeight});
      gap: ${template.gapY} ${template.gapX};
      background: white;
      box-shadow: 0 0 10px rgba(0,0,0,0.1);
      margin: 0 auto;
    `;

    // Generate labels for each asset
    for (let i = 0; i < assets.length; i++) {
      const asset = assets[i];
      if (!asset.codeValue) continue;

      const labelDiv = document.createElement('div');
      labelDiv.style.cssText = `
        width: ${template.labelWidth};
        height: ${template.labelHeight};
        border: 1px dashed #ccc;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 4px;
        box-sizing: border-box;
        overflow: hidden;
      `;

      if (codeType === 'barcode') {
        // Generate barcode
        const canvas = document.createElement('canvas');
        try {
          JsBarcode(canvas, asset.codeValue, {
            format: 'CODE128',
            width: 2,
            height: 40,
            displayValue: true,
            fontSize: 12,
            margin: 2,
          });
          canvas.style.maxWidth = '100%';
          canvas.style.maxHeight = '60%';
          labelDiv.appendChild(canvas);
        } catch (error) {
          console.error('Error generating barcode:', error);
          labelDiv.textContent = 'Error generating barcode';
        }
      } else if (codeType === 'qrcode') {
        // Generate QR code
        const canvas = document.createElement('canvas');
        try {
          await QRCode.toCanvas(canvas, asset.codeValue, {
            width: 80,
            margin: 1,
          });
          canvas.style.maxWidth = '60%';
          canvas.style.maxHeight = '60%';
          labelDiv.appendChild(canvas);
        } catch (error) {
          console.error('Error generating QR code:', error);
          labelDiv.textContent = 'Error generating QR code';
        }
      }

      // Add asset name/ID below the code
      const textDiv = document.createElement('div');
      textDiv.style.cssText = `
        font-size: 8px;
        text-align: center;
        margin-top: 2px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        width: 100%;
      `;
      textDiv.textContent = asset.assetId || asset.name;
      labelDiv.appendChild(textDiv);

      previewContainer.appendChild(labelDiv);
    }

    // Fill remaining labels with empty placeholders
    const totalLabels = template.labelsPerRow * template.labelsPerColumn;
    const remainingLabels = totalLabels - assets.length;
    for (let i = 0; i < remainingLabels; i++) {
      const emptyLabel = document.createElement('div');
      emptyLabel.style.cssText = `
        width: ${template.labelWidth};
        height: ${template.labelHeight};
        border: 1px dashed #e0e0e0;
      `;
      previewContainer.appendChild(emptyLabel);
    }

    previewRef.current.appendChild(previewContainer);
    setPreviewReady(true);
  };

  const handlePrint = () => {
    if (!previewRef.current) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow pop-ups to print labels');
      return;
    }

    const template = AVERY_TEMPLATES.find(t => t.id === selectedTemplate);
    if (!template) return;

    // Clone the preview content and convert canvas elements to images
    const clonedContent = previewRef.current.cloneNode(true) as HTMLElement;
    const canvases = previewRef.current.querySelectorAll('canvas');
    const clonedCanvases = clonedContent.querySelectorAll('canvas');

    // Convert each canvas to an image
    canvases.forEach((canvas, index) => {
      const img = document.createElement('img');
      img.src = canvas.toDataURL('image/png');
      img.style.cssText = canvas.style.cssText;
      img.style.maxWidth = canvas.style.maxWidth || '100%';
      img.style.maxHeight = canvas.style.maxHeight || '100%';
      
      // Replace canvas with image in cloned content
      if (clonedCanvases[index] && clonedCanvases[index].parentNode) {
        clonedCanvases[index].parentNode!.replaceChild(img, clonedCanvases[index]);
      }
    });

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print Asset Labels</title>
          <style>
            @page {
              size: ${template.pageWidth} ${template.pageHeight};
              margin: 0;
            }
            body {
              margin: 0;
              padding: 0;
              font-family: Arial, sans-serif;
            }
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              img {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          ${clonedContent.innerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    
    // Wait for content to load before printing
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);

    toast.success('Print dialog opened');
  };

  const handleDownloadPDF = () => {
    toast.info('PDF download functionality requires a PDF library. Printing to PDF via browser print dialog is recommended.');
    handlePrint();
  };

  if (!hasValidCodeTypes) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cannot Print Labels</DialogTitle>
            <DialogDescription>
              {codeTypes.size === 0 || codeTypes.has(undefined) ? (
                <>
                  One or more of the selected assets do not have barcode or QR code information.
                  <br />
                  <br />
                  Please add barcode or QR code to these assets before printing labels.
                </>
              ) : (
                <>
                  The selected assets have mixed code types (barcode and QR code).
                  Please select assets with the same code type to print labels.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button onClick={onClose}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b bg-white dark:bg-black">
          <DialogTitle>Print Asset Labels</DialogTitle>
          <DialogDescription>
            Select a label template and preview how your {assets.length} asset label{assets.length !== 1 ? 's' : ''} will appear.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 flex gap-6 px-6 py-4 overflow-hidden">
          {/* Template Selection - Left Side */}
          <div className="w-80 flex-shrink-0">
            <div className="h-full flex flex-col">
              <Label className="mb-3">Select Label Template</Label>
              <div className="flex-1 overflow-y-auto rounded-lg border bg-card p-4 space-y-3">
                <RadioGroup value={selectedTemplate} onValueChange={setSelectedTemplate} className="space-y-3">
                  {AVERY_TEMPLATES.map((template) => (
                    <div
                      key={template.id}
                      className={`flex items-start gap-2.5 rounded-lg border p-3 transition-colors ${
                        selectedTemplate === template.id
                          ? 'bg-primary/10 border-primary hover:bg-primary/15'
                          : 'border-border hover:bg-accent'
                      }`}
                    >
                      <div className="pt-1">
                        <RadioGroupItem value={template.id} id={template.id} />
                      </div>
                      <Label htmlFor={template.id} className="flex-1 min-w-0 cursor-pointer block pl-2.5">
                        <div className="font-semibold">
                          {template.name}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          {template.description}
                        </div>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </div>
          </div>

          {/* Preview - Right Side */}
          <div className="flex-1 flex flex-col min-w-0">
            <Label className="mb-3">Preview</Label>
            <div className="flex-1 border rounded-lg bg-muted/50 overflow-auto p-4">
              {!previewReady && (
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground">Generating preview...</p>
                </div>
              )}
              <div ref={previewRef} className="min-h-full" />
            </div>
          </div>
        </div>

        {/* Footer - Fixed at Bottom */}
        <div className="border-t bg-white dark:bg-black px-6 py-4">
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="outline" onClick={handleDownloadPDF}>
              <Download className="mr-2 h-4 w-4" />
              Download PDF
            </Button>
            <Button onClick={handlePrint} disabled={!previewReady}>
              <Printer className="mr-2 h-4 w-4" />
              Print
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
